import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { cn } from '@/lib/utils';
import { isGerman } from '@/i18n/locale';
import { Icon } from '@/components/ui/icons';

interface Props {
  onSend: (text: string) => void;
  disabled?: boolean;
  placeholder?: string;
}

export function InputBar({ onSend, disabled, placeholder }: Props) {
  const de = isGerman();
  const [value, setValue] = useState('');
  const ref = useRef<HTMLTextAreaElement>(null);

  // Auto-grow up to a sensible cap
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 180) + 'px';
  }, [value]);

  const submit = () => {
    const trimmed = value.trim();
    if (!trimmed || disabled) return;
    onSend(trimmed);
    setValue('');
  };

  const onKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  return (
    <div className="shrink-0 border-t border-primary-50 bg-cream/95 backdrop-blur supports-[backdrop-filter]:bg-cream/80">
      <div className="mx-auto max-w-chat px-4 py-3 pb-[max(env(safe-area-inset-bottom),0.75rem)]">
        <div className="flex items-end gap-2 rounded-2xl bg-white border border-primary-100 px-3 py-2 shadow-sm focus-within:border-primary-300 focus-within:ring-2 focus-within:ring-primary-100 transition-shadow">
          <textarea
            ref={ref}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={onKey}
            disabled={disabled}
            rows={1}
            placeholder={placeholder ?? (de ? 'Frag alles über dein Baby…' : 'Ask anything about your baby…')}
            aria-label={de ? 'Nachricht an Alma' : 'Message to Alma'}
            className="flex-1 resize-none bg-transparent px-2 py-2 text-[15px] placeholder:text-slate-muted/80 focus:outline-none focus-visible:outline-none disabled:opacity-50 max-h-[180px]"
          />
          <button
            type="button"
            onClick={submit}
            disabled={disabled || !value.trim()}
            className={cn(
              'shrink-0 rounded-xl bg-primary text-white w-10 h-10 flex items-center justify-center transition-[background-color,transform,opacity]',
              'disabled:opacity-30 disabled:cursor-not-allowed',
              !disabled && value.trim() && 'hover:bg-primary-600 active:scale-95'
            )}
            aria-label={de ? 'Senden' : 'Send'}
          >
            <Icon.ArrowUp size={18} strokeWidth={2.5} />
          </button>
        </div>
        <div className="text-[11px] text-slate-muted mt-1.5 px-1">
          {de
            ? 'Alma informiert — sie ersetzt nicht deine Kinderärztin.'
            : 'Alma is informational, not a substitute for your pediatrician.'}
        </div>
      </div>
    </div>
  );
}
